import clsx from "clsx";
import Link from "next/link";
import { memo, ReactNode, useEffect, useState } from "react";

import {
  DashboardChartData,
  DashboardData,
  OrderStatus,
  ServiceType,
} from "@/apis/clients";
import { ApiResponseError } from "@/apis/errors";
import reservationApi from "@/apis/reservation/reservationApi";
import { Button } from "@/components/atoms/buttons/Button";
import FlowExplanationModal from "@/components/organisms/mypage/FlowExplanationModal";
import { BarGraphChecklist } from "@/foundations/chartJS/Graph";
import {
  getMyPageAcceptanceChart,
  getMyPageSalesChart,
} from "@/foundations/chartJS/utils";
import useApiResponseErrorHandler from "@/hooks/useApiResponseErrorHandler";
import useWindowSize from "@/hooks/useWindowSize";
import { getCookie, setCookie } from "@/libs/service/cookie";
import { useLoading } from "@/states/loading/operations";
import { NEXT_URL } from "@/utils/const";
import { DateFormat, format } from "@/utils/date";

import ChevronRightIc from "/public/icon/chevron-right.svg";
import DecreaseIc from "/public/icon/decrease_ic.svg";
import HelpIc from "/public/icon/header/help_ic.svg";
import IncreaseIc from "/public/icon/increase_ic.svg";
import ShortcutView from "@/components/organisms/mypage/ShortcutView";

enum ChartViewType {
  SaleDate = 0,
  AcceptDate = 1,
}

const ChartViewCookie = "chart_type";

interface Props {
  getOverviewData: () => Promise<DashboardData>;
  getChartData: (start: Date, end: Date) => Promise<DashboardChartData[]>;
  afterShortcuts?: ReactNode;
}

const MyPageOverview = ({
  getOverviewData,
  getChartData,
  afterShortcuts,
}: Props) => {
  const withLoading = useLoading();
  const { handlePageError } = useApiResponseErrorHandler();
  const { isSp } = useWindowSize();

  const [isClient, setIsClient] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isOpenFlow, setIsOpenFlow] = useState(false);
  const [hasUnpaidSubscription, setHasUnpaidSubscription] = useState(false);
  const [chartView, setChartView] = useState(ChartViewType.SaleDate);
  const [overview, setOverview] = useState<DashboardData>({
    total_customers: 0,
    index_customers: 0,
    total_subscribers: 0,
    index_subscribers: 0,
    latest_sales: 0,
    latest_applications: 0,
    total_services: 0,
  });
  const [salesBarGraphSet, setSalesBarGraphSet] = useState(
    getMyPageSalesChart([], [], [], []),
  );
  const [acceptanceBarGraphSet, setAcceptanceBarGraphSet] = useState(
    getMyPageAcceptanceChart([], [], [], []),
  );

  const handleChangeChartView = (type: ChartViewType) => {
    if (chartView !== type) {
      setChartView(type);
      setCookie(`${ChartViewCookie}=${type}`);
    }
  };

  useEffect(() => {
    const chart_type = getCookie(ChartViewCookie);
    if (chart_type !== "") {
      if (Number(chart_type) === ChartViewType.SaleDate) {
        handleChangeChartView(ChartViewType.SaleDate);
      } else if (Number(chart_type) === ChartViewType.AcceptDate) {
        handleChangeChartView(ChartViewType.AcceptDate);
      }
    }
    setIsClient(true);
  }, []);

  useEffect(() => {
    const fetchEvents = async () => {
      const today = new Date();
      const last6Month = new Date(
        today.getFullYear(),
        today.getMonth() - 6,
        today.getDate(),
      );
      const last4Month = new Date(
        today.getFullYear(),
        today.getMonth() - 4,
        today.getDate(),
      );
      const next2Month = new Date(
        today.getFullYear(),
        today.getMonth() + 2,
        today.getDate(),
      );

      await withLoading(async () => {
        setIsReady(false);
        try {
          const [overviewData, chartData, unpaidSubscriptionResult] = await Promise.all([
            getOverviewData(),
            getChartData(last6Month, next2Month),
            reservationApi.indexReservation({
              serviceType: ServiceType.SUBSCRIPTION_SERVICE,
              orderStatus: OrderStatus.UNPAID,
              page: 1,
              perPage: 1,
            }),
          ]);

          setOverview(overviewData);
          setHasUnpaidSubscription((unpaidSubscriptionResult.pagination?.total ?? 0) > 0);

          const convertChartData = (
            chartData: DashboardChartData[],
            from?: Date,
            to?: Date,
          ) => {
            const labels: string[] = [];
            const customers: number[] = [];
            const revenue: number[] = [];
            const futureRevenue: number[] = [];
            const officialRevenueData: number[] = [];
            const temporaryRevenueData: number[] = [];

            chartData
              .filter((value) => {
                const date = new Date(value.month);
                if (from && to) return date >= from && date <= to;
                if (from) return date >= from;
                if (to) return date <= to;
                return true;
              })
              .forEach((value) => {
                const date = new Date(value.month);
                labels.push(format(date, DateFormat.MONTH_DATE_JP));
                if (date.getMonth() < today.getMonth()) {
                  customers.push(value.customers);
                  revenue.push(value.revenue);
                  futureRevenue.push(0);

                  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                  officialRevenueData.push(value.official_revenue);
                  temporaryRevenueData.push(value.temporary_revenue);
                } else if (date.getMonth() === today.getMonth()) {
                  // customers.push(value.customers);
                  revenue.push(value.revenue);
                  futureRevenue.push(value.future_revenue);

                  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                  officialRevenueData.push(value.official_revenue);
                  temporaryRevenueData.push(value.temporary_revenue);
                } else {
                  revenue.push(0);
                  futureRevenue.push(value.future_revenue);

                  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                  officialRevenueData.push(value.official_revenue);
                  temporaryRevenueData.push(value.temporary_revenue);
                }
              });
            return { labels, customers, revenue, futureRevenue, officialRevenueData, temporaryRevenueData };
          };



          const salesData = convertChartData(chartData, last4Month, next2Month);
          setSalesBarGraphSet(
            getMyPageSalesChart(
              salesData.labels,
              salesData.customers,
              salesData.revenue,
              salesData.futureRevenue,
            ),
          );
          const acceptanceData = convertChartData(chartData, last6Month, today);
          setAcceptanceBarGraphSet(
            getMyPageAcceptanceChart(
              acceptanceData.labels,
              acceptanceData.customers,
              acceptanceData.officialRevenueData,
              acceptanceData.temporaryRevenueData,
              // acceptanceData.revenue,
            ),
          );
        } catch (e) {
          if (e instanceof ApiResponseError) {
            await handlePageError(e);
          }
        }
        setIsReady(true);
      });
    };
    void fetchEvents();
  }, []);

  const FIRST_DATA_ANALYST = [
    {
      id: 1,
      label: "総顧客数",
      value: overview.total_customers,
      mtm: overview.index_customers,
    },
    {
      id: 2,
      label: "メルマガ購読者数",
      value: overview.total_subscribers,
      mtm: overview.index_subscribers,
    },
  ];

  const SECOND_DATA_ANALYST = [
    {
      id: 1,
      label: "昨日の売上（受付日基準）",
      value: overview.latest_sales,
      suffix: "円",
      linkLabel: "売上管理へ",
      link: NEXT_URL.EARNINGS,
    },
    {
      id: 2,
      label: "昨日の受付・申込数",
      value: overview.latest_applications,
      suffix: "件",
      linkLabel: "予約管理へ",
      link: NEXT_URL.RESERVATIONS,
    },
  ];

  if (!isClient) {
    return <></>;
  }

  return (
    <>
      <ShortcutView
        isReady={isReady}
        hasServices={overview.total_services > 0}
        hasUnpaidSubscription={hasUnpaidSubscription}
      />
      {afterShortcuts && <div className="mt-10">{afterShortcuts}</div>}
      <div
        className={clsx(
          "grid grid-cols-2 gap-6 lg:grid-cols-4",
          afterShortcuts ? "mt-10" : "mt-6",
        )}
      >
        <div className="flex flex-col justify-between gap-2 col-span-2 md:col-span-1 rounded-[10px] pt-[28px] pb-[39px] pl-[38px] border-[1px] border-solid border-[#E5E7EB]">
          {FIRST_DATA_ANALYST.map((item) => (
            <div className="" key={item.id}>
              <p className="text-[#757575] font-bold text-[10px] md:text-sm">
                {item.label}
              </p>
              <p className="text-black font-bold text-base ml-[-1px] mt-1 mb-[7px]">
                <strong className="text-black text-base md:text-[40px] leading-[47px] font-bold">
                  {Number(item.value).toLocaleString()}
                </strong>
                名
              </p>
              <p className="flex items-center gap-x-[2px] text-sm text-black">
                前月比
                {item.mtm === 0 && (
                  <>
                    <span className="ml-2 font-bold">--</span>
                  </>
                )}
                {item.mtm > 0 && (
                  <>
                    <IncreaseIc className="ml-[2px]" />
                    <span className="text-primary-medium font-bold">
                      {Number(item.mtm).toLocaleString()}
                    </span>
                  </>
                )}
                {item.mtm < 0 && (
                  <>
                    <DecreaseIc className="ml-[2px]" />
                    <span className="text-[#BC0000] font-bold">
                      {Number(-item.mtm).toLocaleString()}
                    </span>
                  </>
                )}
              </p>
            </div>
          ))}
        </div>

        <div className="flex flex-col justify-between gap-2 col-span-2 md:col-span-1 rounded-[10px] pt-[28px] pb-[36px] pl-[33px] border-[1px] border-solid border-[#E5E7EB]">
          {SECOND_DATA_ANALYST.map((item) => (
            <div className="" key={item.id}>
              <p className="text-[#757575] font-bold text-[10px] md:text-sm md:leading-tight">
                {item.label}
              </p>
              <p className="text-black font-bold text-base ml-[-5px] mt-[1px] mb-[7px]">
                <strong className="text-black text-base md:text-[40px] leading-[47px] font-bold">
                  {Number(item.value).toLocaleString()}
                </strong>
                {item.suffix}
              </p>
              <Link
                href={item.link}
                className="flex items-center gap-x-1 text-sm text-black cursor-pointer">

                {item.linkLabel}
                <ChevronRightIc width="24" fill="#41C9B4" />

              </Link>
            </div>
          ))}
        </div>

        <div className="col-span-4 md:col-span-2 p-4 md:p-[20px] rounded-[10px] border-[1px] border-solid border-[#E5E7EB]">
          <div className="flex items-center gap-1 mb-2">
            <h3 className="text-[#757575] text-sm font-bold leading-tight">
              直近6ヶ月の売上・顧客推移
            </h3>
            <button onClick={() => setIsOpenFlow(true)}>
              <HelpIc
                width={20}
                height={20}
                viewBox={"6 6 24 24"}
                fill={"#626262"}
              />
              {isOpenFlow && (
                <FlowExplanationModal
                  isOpen={isOpenFlow}
                  handleClose={() => setIsOpenFlow(false)}
                />
              )}
            </button>
          </div>
          <div className="flex items-center mb-2">
            <Button
              label="売上計上日基準"
              color={
                chartView === ChartViewType.SaleDate ? "primary" : "bg-white"
              }
              className={clsx(
                "flex-1 !h-6 !text-xs !rounded-l !rounded-r-none !py-[6px] !px-[8px]",
                chartView ? "!font-bold" : "!font-normal",
              )}
              onClick={() => handleChangeChartView(ChartViewType.SaleDate)}
            />
            <Button
              label="受付日基準"
              color={
                chartView === ChartViewType.AcceptDate ? "primary" : "bg-white"
              }
              className={clsx(
                "flex-1 !h-6 !text-xs !rounded-l-none !rounded-r !py-[6px] !px-[8px]",
                !chartView ? "!font-bold" : "!font-normal",
              )}
              onClick={() => handleChangeChartView(ChartViewType.AcceptDate)}
            />
          </div>
          {chartView === ChartViewType.SaleDate ? (
            <BarGraphChecklist
              labels={salesBarGraphSet.labels}
              datasets={salesBarGraphSet.datasets}
              isSp={isSp}
            />
          ) : (
            <BarGraphChecklist
              labels={acceptanceBarGraphSet.labels}
              datasets={acceptanceBarGraphSet.datasets}
              isSp={isSp}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default memo(MyPageOverview);
